import { tint } from '../report-aggregation';

/**
 * The band of captions under a ladder or an explainer, drawn as one arrow.
 *
 * Shared by the two modules that draw it. The CSS stays duplicated in each -- view encapsulation
 * stops one component's stylesheet reaching the other -- but the geometry is arithmetic, and two
 * copies of arithmetic is how two pages of the same deck end up with different arrows.
 *
 * **It is one bar, not a row of blocks.** A single continuous gradient runs the whole width, with an
 * arrowhead at each end, and the captions sit on it. Nothing marks the boundaries between them:
 * the colour changing under a caption is the boundary, and anything drawn there -- a divider, a
 * chevron -- turns one spectrum back into a row of labels butted together, which is the opposite of
 * what this says.
 *
 * **It points outward from the middle.** The left end is an arrowhead pointing left and the right
 * end one pointing right, because a spectrum runs away from its centre in both directions -- one
 * end is where things are best and the other where they are worst, and a single arrow pointing one
 * way would say the whole thing flows that way.
 */

/**
 * How far each end comes to a point.
 *
 * Deep enough to read as an arrowhead rather than a bevelled corner: against a band around 40px
 * tall, a shallow point is just a cut corner, and what makes an arrow an arrow is that the point is
 * longer than it is a nick. Roughly the band's own height is where it starts looking deliberate.
 *
 * The stylesheets hold the end captions clear of this by the same distance -- see `.band:first-child`
 * in either module -- so a change here needs a change there.
 */
const HEAD = 36;

/**
 * How far the head stands proud of the shaft, above and below.
 *
 * This is what makes it an arrow. A head cut *within* the bar's height is a tapered bar -- the ends
 * come to a point, but nothing about them says arrow, and it read as a ribbon that had been
 * sharpened. A real arrowhead is wider than the shaft it sits on, so the shaft is inset by this
 * much top and bottom and the head flares back out to the full height.
 *
 * The stylesheets pad the bar by the same amount, so the captions sit inside the shaft rather than
 * across the flare -- see `.arrow` in stage-card.ts. A change here needs a change there.
 */
export const SHAFT_INSET = 7;

/** How far the bar lightens past its last colour, having nothing further to blend into. */
const TAIL_TINT = 0.28;

/** One caption on the bar. */
export interface SpectrumBand {
  text: string;
  /** Its width relative to the others. */
  grow: number;
}

/** The bar, ready to draw. */
export interface Spectrum {
  /** The gradient across the whole width, so the colour changes continuously rather than in steps. */
  background: string;
  /** The arrowhead at each end. */
  clipPath: string;
  bands: SpectrumBand[];
}

/**
 * The bar and its captions.
 *
 * `fillFor` gives a caption its colour -- the modules take theirs from different places, and neither
 * needs to know how the other does it. `weights` sizes the captions against each other, which the
 * ladder needs so a caption sits over the stage it names; left out, they share the width evenly.
 */
export function spectrumOf(
  labels: readonly string[],
  fillFor: (index: number) => string,
  weights?: readonly number[],
): Spectrum {
  const count = labels.length;
  if (!count) return { background: 'none', clipPath: 'none', bands: [] };

  const grows = labels.map((unused, index) => Math.max(1, weights?.[index] ?? 1));

  return {
    background: gradient(grows, fillFor),
    // A single caption is not a direction, so it keeps square ends.
    clipPath: count < 2 ? 'none' : arrow(),
    bands: labels.map((text, index) => ({ text, grow: grows[index] })),
  };
}

/**
 * The shape: a shaft between two arrowheads that stand proud of it, clockwise from where the shaft
 * meets the left-hand head.
 */
function arrow(): string {
  const head = `${HEAD}px`;
  const rightHead = `calc(100% - ${HEAD}px)`;
  const top = `${SHAFT_INSET}px`;
  const bottom = `calc(100% - ${SHAFT_INSET}px)`;

  return `polygon(${[
    `${head} ${top}`,          // shaft, top left
    `${rightHead} ${top}`,     // shaft, top right
    `${rightHead} 0`,          // the right head flares up
    '100% 50%',                // and comes to its point
    `${rightHead} 100%`,       // and back down
    `${rightHead} ${bottom}`,  // shaft, bottom right
    `${head} ${bottom}`,       // shaft, bottom left
    `${head} 100%`,            // the left head flares down
    '0 50%',                   // its point
    `${head} 0`,               // and back up
  ].join(', ')})`;
}

/**
 * One gradient across the whole bar, each colour placed at the centre of the caption it belongs to
 * so the caption sits on its own colour and the change happens between them. The ends are pinned:
 * the first colour reaches the left point, and the last lightens toward the right one rather than
 * wrapping back to the first, which would end the arrow on the colour it started with.
 */
function gradient(grows: readonly number[], fillFor: (index: number) => string): string {
  const total = grows.reduce((sum, grow) => sum + grow, 0);
  const stops: string[] = [`${fillFor(0)} 0%`];

  let before = 0;
  grows.forEach((grow, index) => {
    const centre = ((before + grow / 2) / total) * 100;
    before += grow;
    // The ends are already pinned, so only the middles need placing.
    if (index > 0 && index < grows.length - 1) stops.push(`${fillFor(index)} ${centre.toFixed(2)}%`);
  });

  const last = fillFor(grows.length - 1);
  if (grows.length > 1) stops.push(last);
  stops.push(`${tint(last, TAIL_TINT)} 100%`);

  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

/**
 * A band's captions with one of them changed, padded to reach it, as the ladder and the explainer
 * both store theirs: one per stage or panel, by position, with no trailing blanks kept.
 */
export function withCaption(stored: readonly string[] | undefined, index: number, caption: string): string[] {
  const captions = Array.from({ length: Math.max(stored?.length ?? 0, index + 1) }, (_, at) => stored?.[at] ?? '');
  captions[index] = caption;
  while (captions.length && !captions[captions.length - 1]) captions.pop();
  return captions;
}
